import { FormEvent, useCallback, useEffect, useState } from "react";
import { ArrowRight, ChevronRight, LoaderCircle, Search } from "lucide-react";

import { ApiError, getDashboard, getLeads, getSession, login, logout } from "./api";
import { Brand, Header, Sidebar, type View } from "./AdminChrome";
import { AvailabilityPage } from "./AvailabilityPage";
import { BookingsPage } from "./BookingsPage";
import { CatalogPage } from "./CatalogPage";
import { ClientsPage } from "./ClientsPage";
import { LeadDetailPanel } from "./LeadDetailPanel";
import { QuickBooksPage } from "./QuickBooksPage";
import { NotificationsPage } from "./NotificationsPage";
import { ReportsPage } from "./ReportsPage";
import { SchedulePage } from "./SchedulePage";
import { SquarePage } from "./SquarePage";
import type { AdminUser, DashboardMetrics, Lead } from "./types";
import { useVisiblePolling } from "./use-visible-polling";
import { WorkforcePage } from "./WorkforcePage";

export function App() {
  const [user, setUser] = useState<AdminUser | null>();
  const [view, setView] = useState<View>(initialView);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [view]);

  useEffect(() => {
    void getSession()
      .then(setUser)
      .catch(() => setUser(null));
  }, []);

  if (user === undefined) return <LoadingScreen />;
  if (user === null) return <LoginScreen onAuthenticated={setUser} />;
  const canWrite = user.roles.some((role) => ["OWNER", "ADMIN", "MANAGER"].includes(role));
  const canManageIntegrations = user.roles.some((role) => ["OWNER", "ADMIN"].includes(role));

  return (
    <div className="admin-layout">
      <Sidebar
        current={view}
        open={menuOpen}
        onNavigate={(next) => {
          setView(next);
          setMenuOpen(false);
        }}
        onClose={() => setMenuOpen(false)}
      />
      <main className="main-shell">
        <Header
          user={user}
          onMenu={() => setMenuOpen(true)}
          onLogout={async () => {
            await logout();
            setUser(null);
          }}
        />
        {view === "dashboard" ? (
          <Dashboard onOpenLeads={() => setView("leads")} />
        ) : view === "reports" ? (
          <ReportsPage />
        ) : view === "leads" ? (
          <LeadInbox />
        ) : view === "clients" ? (
          <ClientsPage canWrite={canWrite} />
        ) : view === "bookings" ? (
          <BookingsPage canWrite={canWrite} />
        ) : view === "catalog" ? (
          <CatalogPage canWrite={canWrite} />
        ) : view === "schedule" ? (
          <SchedulePage />
        ) : view === "availability" ? (
          <AvailabilityPage canWrite={canWrite} />
        ) : view === "quickbooks" ? (
          <QuickBooksPage canManage={canManageIntegrations} />
        ) : view === "square" ? (
          <SquarePage />
        ) : view === "notifications" ? (
          <NotificationsPage canWrite={canWrite} />
        ) : (
          <WorkforcePage section={view} canWrite={canWrite} />
        )}
      </main>
    </div>
  );
}

function initialView(): View {
  if (window.location.pathname === "/quickbooks") return "quickbooks";
  if (window.location.pathname === "/square") return "square";
  if (window.location.pathname === "/notifications") return "notifications";
  if (window.location.pathname === "/reports") return "reports";
  if (new URLSearchParams(window.location.search).has("quickbooks")) return "quickbooks";
  if (new URLSearchParams(window.location.search).has("square")) return "square";
  if (new URLSearchParams(window.location.search).has("reports")) return "reports";
  return "dashboard";
}

function LoginScreen({ onAuthenticated }: { onAuthenticated: (user: AdminUser) => void }) {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSending(true);
    setError(false);
    const fields = new FormData(event.currentTarget);
    try {
      onAuthenticated(await login(String(fields.get("email")), String(fields.get("password"))));
    } catch {
      setError(true);
      setSending(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-story" aria-label="TLC Cleaning Co.">
        <Brand />
        <div className="story-copy">
          <span className="eyebrow">Private operations workspace</span>
          <h1>Thoughtful service, organized beautifully.</h1>
          <p>
            One secure place for every inquiry, customer conversation, and carefully scheduled
            visit.
          </p>
        </div>
        <p className="story-footnote">Tender · Love · Care — in every detail.</p>
      </section>
      <section className="login-panel">
        <form className="login-card" onSubmit={submit}>
          <span className="eyebrow lime">Welcome back</span>
          <h2>Sign in to TLC Operations</h2>
          <p>Use your authorized team account to continue.</p>
          <label>
            Email address
            <input name="email" type="email" autoComplete="username" required />
          </label>
          <label>
            Password
            <input name="password" type="password" autoComplete="current-password" required />
          </label>
          {error ? (
            <p className="form-error" role="alert">
              Email or password not recognized. Please try again.
            </p>
          ) : null}
          <button className="primary-button" disabled={sending}>
            {sending ? <LoaderCircle className="spin" /> : null}
            Sign in <ArrowRight />
          </button>
          <p className="security-note">Protected access · Sessions expire automatically</p>
        </form>
      </section>
    </main>
  );
}

function Dashboard({ onOpenLeads }: { onOpenLeads: () => void }) {
  const [metrics, setMetrics] = useState<DashboardMetrics>();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const [nextMetrics, leadPage] = await Promise.all([getDashboard(), getLeads()]);
      setMetrics(nextMetrics);
      setLeads(leadPage.items.slice(0, 5));
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => void load(), [load]);
  useVisiblePolling(load, 30_000);

  return (
    <div className="page-content">
      <PageIntro
        eyebrow="Daily overview"
        title="Good morning. Here’s what needs your care."
        copy="A focused view of new inquiries and the conversations moving toward a booking."
      />
      {failed ? <LoadError onRetry={load} /> : null}
      <section className="metric-grid" aria-label="Lead summary">
        <Metric label="New leads" value={metrics?.newLeads} tone="lime" />
        <Metric label="Open conversations" value={metrics?.openLeads} />
        <Metric label="Quotes pending" value={metrics?.quotePending} />
        <Metric label="Awaiting customer" value={metrics?.waitingCustomer} />
      </section>
      <section className="content-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Most recent</span>
            <h2>New inquiries</h2>
          </div>
          <button className="text-button" onClick={onOpenLeads}>
            View all leads <ArrowRight />
          </button>
        </div>
        <LeadTable leads={leads} loading={!metrics && !failed} onOpen={onOpenLeads} />
      </section>
    </div>
  );
}

function LeadInbox() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [cursor, setCursor] = useState<string | null>();
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [selectedLead, setSelectedLead] = useState<string>();

  const load = useCallback(async (next?: string) => {
    setLoading(true);
    try {
      const page = await getLeads(next);
      setLeads((current) => (next ? [...current, ...page.items] : page.items));
      setCursor(page.nextCursor);
      setFailed(false);
    } catch (error) {
      if (!(error instanceof ApiError && error.status === 401)) setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => void load(), [load]);
  useVisiblePolling(load, 30_000);

  return (
    <div className="page-content">
      <PageIntro
        eyebrow="Lead pipeline"
        title="Every inquiry, ready for a thoughtful response."
        copy="Review contact details, requested services, and the next step for each customer."
      />
      <section className="content-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Inbox</span>
            <h2>All leads</h2>
          </div>
          <div className="search-shell" aria-label="Search coming soon">
            <Search /> <span>Search leads</span>
          </div>
        </div>
        {failed ? <LoadError onRetry={() => load()} /> : null}
        <LeadTable
          leads={leads}
          loading={loading && leads.length === 0}
          onOpen={(lead) => setSelectedLead(lead.id)}
        />
        {cursor ? (
          <button className="secondary-button load-more" onClick={() => void load(cursor)}>
            {loading ? <LoaderCircle className="spin" /> : null} Load more
          </button>
        ) : null}
      </section>
      {selectedLead ? (
        <LeadDetailPanel
          leadId={selectedLead}
          onClose={() => setSelectedLead(undefined)}
          onChanged={() => void load()}
        />
      ) : null}
    </div>
  );
}

function PageIntro({ eyebrow, title, copy }: { eyebrow: string; title: string; copy: string }) {
  return (
    <header className="page-intro">
      <span className="eyebrow lime">{eyebrow}</span>
      <h1>{title}</h1>
      <p>{copy}</p>
    </header>
  );
}

function Metric({ label, value, tone }: { label: string; value?: number; tone?: string }) {
  return (
    <article className={`metric-card ${tone ?? ""}`}>
      <span>{label}</span>
      <strong>{value ?? "—"}</strong>
      <small>Current pipeline</small>
    </article>
  );
}

function LeadTable({
  leads,
  loading,
  onOpen,
}: {
  leads: Lead[];
  loading: boolean;
  onOpen?: (lead: Lead) => void;
}) {
  if (loading)
    return (
      <div className="table-state">
        <LoaderCircle className="spin" /> Loading inquiries…
      </div>
    );
  if (leads.length === 0) return <div className="table-state">No leads have arrived yet.</div>;
  return (
    <div className="table-wrap">
      <table className="mobile-card-table">
        <thead>
          <tr>
            <th>Customer</th>
            <th>Service</th>
            <th>Status</th>
            <th>Received</th>
            <th>
              <span className="sr-only">Open</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {leads.map((lead) => (
            <tr key={lead.id}>
              <td data-label="Customer">
                <strong>{lead.name}</strong>
                <span>
                  {lead.email}
                  <br />
                  {lead.phone}
                </span>
              </td>
              <td data-label="Service">{lead.serviceInterest ?? "Not specified"}</td>
              <td data-label="Status">
                <Status value={lead.status} />
              </td>
              <td data-label="Received">{formatDate(lead.createdAt)}</td>
              <td data-label="">
                <button
                  className="row-action"
                  aria-label={`Open ${lead.name}`}
                  onClick={() => onOpen?.(lead)}
                  disabled={!onOpen}
                >
                  <ChevronRight />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Status({ value }: { value: string }) {
  return (
    <span className={`status status-${value.toLowerCase()}`}>{value.replaceAll("_", " ")}</span>
  );
}
function LoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="load-error" role="alert">
      We couldn’t refresh this view.<button onClick={onRetry}>Try again</button>
    </div>
  );
}
function LoadingScreen() {
  return (
    <div className="loading-screen">
      <Brand />
      <LoaderCircle className="spin" />
    </div>
  );
}
function formatDate(value: string) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}
